import React, { InputHTMLAttributes, useState, useEffect, forwardRef, useRef } from 'react';
import clsx from 'clsx';
import DatePicker, { registerLocale } from 'react-datepicker';
import * as locales from 'date-fns/locale'; // Import all locales
import 'react-datepicker/dist/react-datepicker.css';
import '../../styles/datepicker-overrides.css'; // Import overrides AFTER default
import { parse, isValid } from 'date-fns';
import CalendarIcon from '../icons/CalendarIcon';

// Helper to get browser locale and map it to date-fns locale object
const getBrowserLocale = () => {
  const browserLang = navigator.language ?? 'en-US'; // Default to en-US
  // Find matching date-fns locale (handle variations like en-GB, en-US)
  const localeKey = Object.keys(locales).find(
    (key) => key.replace('_', '').toLowerCase() === browserLang.replace('-', '').toLowerCase()
  ) as keyof typeof locales | undefined;

  const localeObj = localeKey ? locales[localeKey] : locales.enUS; // Default to enUS
  const localeCode = localeKey ?? 'enUS';

  // Register the locale with react-datepicker if it hasn't been already
  try {
    registerLocale(localeCode, localeObj);
  } catch {
    // Locale might already be registered, ignore error
  }

  // Attempt to get a locale-specific short date format for placeholder
  // This is complex to get perfectly right for all locales automatically
  // We'll use react-datepicker's built-in locale handling which is usually good
  // but placeholder might show a default like MM/dd/yyyy if specific format isn't easily derived
  let placeholderFormat = 'P'; // 'P' is date-fns shorthand for locale-specific short date
  try {
    // Let react-datepicker handle the format based on locale
  } catch {
    placeholderFormat = 'MM/dd/yyyy'; // Fallback placeholder
  }

  return { localeCode, localeObj, placeholderFormat };
};

// Define a specific onChange handler type for date inputs
type DateChangeHandler = (date: Date | null) => void;

// Exclude the type property as we'll define it with specific allowed values
// Omit onChange and value as well, to redefine them conditionally
type InputPropsBase = Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'onChange' | 'value'>;

interface BaseInputTextProps extends InputPropsBase {
  label: string;
  id: string;
  error?: string;
  containerClassName?: string;
  maxAllowedDate?: Date | null; // Add maxAllowedDate to the base props
}

// Conditional Props for onChange based on type
interface TextInputProps extends BaseInputTextProps {
  type?: 'text';
  onChange?: React.ChangeEventHandler<HTMLInputElement>; // Standard change event for text
  value?: string | number | readonly string[]; // Standard input values
}

interface DateInputProps extends BaseInputTextProps {
  type: 'date';
  onChange?: DateChangeHandler; // Custom handler for date
  value?: Date | null; // Date value
  maxAllowedDate?: Date | null;
}

// Union type for all possible props
type InputTextProps = TextInputProps | DateInputProps;

/**
 * A text or date input component with a floating label, based on Figma design.
 */
const InputText: React.FC<InputTextProps> = ({
  label,
  id,
  required,
  error,
  className = '',
  containerClassName = '',
  value,
  defaultValue,
  placeholder = '',
  type = 'text',
  onChange,
  onFocus,
  onBlur,
  disabled,
  maxAllowedDate,
  ...props
}) => {
  const [isFocused, setIsFocused] = useState(false);
  // Initial value depends on type
  const initialValue = value !== undefined ? value : type === 'text' ? defaultValue : undefined;
  const [hasValue, setHasValue] = useState(
    initialValue !== undefined && initialValue !== null && initialValue !== ''
  );
  const labelText = required ? `${label} *` : label;
  const hasError = !!error;

  const datePickerRef = useRef<DatePicker>(null);
  const [browserLocaleInfo, setBrowserLocaleInfo] = useState({
    localeCode: 'enUS',
    placeholderFormat: 'MM/DD/YYYY',
  });

  useEffect(() => {
    if (type === 'date') {
      setBrowserLocaleInfo(getBrowserLocale());

      // Ensure the datepicker portal container exists
      const portalId = 'datepicker-portal';
      let portalElement = document.getElementById(portalId);
      if (!portalElement) {
        portalElement = document.createElement('div');
        portalElement.id = portalId;
        document.body.appendChild(portalElement);
      }
    }
    // No cleanup needed for this simple approach,
    // portal element stays for potential reuse by other datepickers.
  }, [type]);

  // Update hasValue when the value prop changes
  useEffect(() => {
    setHasValue(value !== undefined && value !== null && value !== '');
  }, [value]);

  // --- Handlers ---
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setHasValue(!!e.target.value);
    // Check if onChange exists and is the correct type for text
    if (type === 'text' && onChange) {
      (onChange as React.ChangeEventHandler<HTMLInputElement>)(e);
    }
  };

  // Pass Date | null directly to the specific DateChangeHandler
  const handleDateChange = (date: Date | null) => {
    setHasValue(!!date);
    // Check if onChange exists and is the correct type for date
    if (type === 'date' && onChange) {
      (onChange as DateChangeHandler)(date);
    }
  };

  const handleFocusInternal = (e: React.FocusEvent<HTMLInputElement>) => {
    // Check if the input type is 'date' and ensure the focus behavior doesn't block the DatePicker
    if (type === 'date') {
      return; // Prevent interfering with the default focus behavior
    }
    setIsFocused(true);
    if (onFocus) {
      onFocus(e);
    }
  };

  const handleBlurInternal = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(false);
    if (onBlur) {
      onBlur(e);
    }
  };

  const CustomDateInput = forwardRef<
    HTMLInputElement,
    { value?: string; onClick?: () => void; placeholder?: string }
  >(({ value: dateValue, placeholder: ph }, ref) => {
    // Keep track of the input value separate from the datepicker value
    const [inputValue, setInputValue] = useState(dateValue ?? '');

    // Use effect to update internal input value when datepicker changes externally
    useEffect(() => {
      if (dateValue) setInputValue(dateValue);
    }, [dateValue]);

    const formatDateInput = (value: string) => {
      // Remove any non-digits
      const digits = value.replace(/\D/g, '');

      // Format based on the number of digits
      if (digits.length <= 2) {
        return digits;
      } else if (digits.length <= 4) {
        return `${digits.substring(0, 2)}/${digits.substring(2)}`;
      } else {
        return `${digits.substring(0, 2)}/${digits.substring(2, 4)}/${digits.substring(4, 8)}`;
      }
    };

    // Handle changes to the input
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const rawValue = e.target.value;

      // Special case: if backspace was just used, handle differently
      if (rawValue.length < inputValue.length) {
        setInputValue(rawValue);
        setHasValue(!!rawValue);
        return;
      }

      // Format the value with our mask
      const formattedValue = formatDateInput(rawValue);
      setInputValue(formattedValue);
      setHasValue(!!formattedValue);

      // Try to parse if we have a full date
      if (/^\d{2}\/\d{2}\/\d{4}$/.test(formattedValue)) {
        try {
          const parsedDate = parse(formattedValue, 'MM/dd/yyyy', new Date());
          if (isValid(parsedDate)) {
            handleDateChange(parsedDate);
          }
        } catch {
          // Ignore parsing errors for incomplete dates
        }
      }
    };

    // Handle key presses for Enter and Tab confirmation
    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      // Handle Enter key for date confirmation
      if (e.key === 'Enter' && /^\d{2}\/\d{2}\/\d{4}$/.test(inputValue)) {
        try {
          const parsedDate = parse(inputValue, 'MM/dd/yyyy', new Date());
          if (isValid(parsedDate)) {
            handleDateChange(parsedDate);
            e.preventDefault(); // Prevent form submission on Enter
            e.currentTarget.blur(); // Remove focus
          }
        } catch {
          // Invalid date format, do nothing
        }
      }
      // Let Tab key work normally for navigation - don't interfere with it
    };

    return (
      <input
        ref={ref}
        id={id}
        type="text"
        value={inputValue}
        required={required}
        aria-invalid={hasError}
        aria-describedby={hasError ? `${id}-error` : undefined}
        placeholder={ph ?? 'MM/DD/YYYY'}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={handleFocusInternal}
        onBlur={handleBlurInternal}
        disabled={disabled}
        name={props.name}
        autoComplete="off"
        className={clsx(
          'w-full border rounded-200 p-4 font-sans text-base text-gray-1100 pt-9 pb-3 h-[68px] pr-12',
          !hasError && 'border-black-200',
          !hasError && !disabled && 'hover:border-blue-200',
          'focus:outline-none focus:ring-1',
          !hasError && 'focus:ring-blue-600 focus:border-blue-600',
          hasError && 'focus:ring-destructive-dark focus:border-destructive-dark',
          'disabled:bg-gray-50 disabled:border-black-120 disabled:text-black-300 disabled:cursor-not-allowed',
          hasError && 'border-destructive-dark',
          className
        )}
      />
    );
  });
  CustomDateInput.displayName = 'CustomDateInput';

  // Determine if the label should float up: float if focused, has value, or has a placeholder.
  const effectivePlaceholder = type === 'date' ? placeholder || 'MM/DD/YYYY' : placeholder;
  const hasValueFromProp = value !== undefined && value !== null && value !== '';
  const isLabelFloating = isFocused || hasValue || hasValueFromProp || !!effectivePlaceholder;

  return (
    <div className={clsx('flex flex-col relative', containerClassName)}>
      <label
        htmlFor={id}
        className={clsx(
          'font-sans text-sm font-medium transition-transform duration-200 ease-out pointer-events-none absolute left-4 z-10',
          isLabelFloating
            ? 'transform translate-y-3 text-black-600'
            : 'transform translate-y-6 text-black-600'
        )}
      >
        {labelText}
      </label>

      {type === 'date' ? (
        <div className="relative w-full">
          <DatePicker
            ref={datePickerRef}
            id={id}
            selected={type === 'date' && value instanceof Date ? value : null}
            onChange={handleDateChange}
            onFocus={handleFocusInternal}
            onBlur={handleBlurInternal}
            onCalendarOpen={() => {
              // Focus the calendar popup when it opens
              setTimeout(() => {
                const calendarElement = document.querySelector('.react-datepicker') as HTMLElement;
                if (calendarElement) {
                  // Focus the first focusable element in the calendar
                  const firstFocusable = calendarElement.querySelector(
                    'button, [tabindex]:not([tabindex="-1"])'
                  ) as HTMLElement;
                  firstFocusable?.focus();
                }
              }, 100);
            }}
            locale={browserLocaleInfo.localeCode}
            dateFormat="MM/dd/yyyy"
            required={required}
            disabled={disabled}
            showPopperArrow={false}
            placeholderText={placeholder || 'MM/DD/YYYY'}
            customInput={<CustomDateInput />}
            wrapperClassName="w-full"
            portalId="datepicker-portal"
            popperClassName="z-60"
            popperProps={{
              strategy: 'fixed',
            }}
            shouldCloseOnSelect={true}
            onInputClick={() => {
              /* No-op to prevent opening calendar on input click - calendar opens via icon only */
            }}
            name={props.name}
            autoComplete="off"
            showYearDropdown
            dropdownMode="select"
            yearDropdownItemNumber={100}
            scrollableYearDropdown
            maxDate={maxAllowedDate ?? undefined}
          />
          <button
            type="button"
            className="absolute right-4 top-1/2 -translate-y-1/2 text-primary cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2 rounded p-1"
            onClick={() => {
              // Force open the date picker
              datePickerRef.current?.setOpen(true);
            }}
            onKeyDown={(e) => {
              // Handle Enter and Space key presses
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                datePickerRef.current?.setOpen(true);
              }
            }}
            aria-label="Open calendar"
            tabIndex={disabled ? -1 : 0}
            disabled={disabled}
          >
            <CalendarIcon size={24} />
          </button>
        </div>
      ) : (
        <input
          id={id}
          type="text"
          required={required}
          aria-invalid={hasError}
          aria-describedby={hasError ? `${id}-error` : undefined}
          value={type === 'text' ? (value as string) : ''}
          defaultValue={type === 'text' ? (defaultValue as string) : ''}
          placeholder={placeholder}
          onChange={handleInputChange}
          onFocus={handleFocusInternal}
          onBlur={handleBlurInternal}
          disabled={disabled}
          name={props.name}
          autoComplete={props.autoComplete}
          className={clsx(
            'w-full border rounded-200 p-4 font-sans text-base text-gray-1100 pt-9 pb-3 h-[68px]',
            !hasError && 'border-black-200',
            !hasError && !disabled && 'hover:border-blue-200',
            'focus:outline-none focus:ring-1',
            !hasError && 'focus:ring-blue-600 focus:border-blue-600',
            hasError && 'focus:ring-destructive-dark focus:border-destructive-dark',
            'disabled:bg-gray-50 disabled:border-black-120 disabled:text-black-300 disabled:cursor-not-allowed',
            hasError && 'border-destructive-dark',
            className
          )}
          {...props}
        />
      )}

      {hasError && (
        <span id={`${id}-error`} className="text-xs text-destructive-text-error mt-1 font-medium">
          {error}
        </span>
      )}
    </div>
  );
};

export default InputText;
